<template>
  <div class="message-center">
    <!-- 头部 -->
    <div class="message-header">
      <h1 class="title"><i class="fas fa-envelope"></i> {{ $t('_xx._bt') }}</h1>
      <span class="unread-badge" v-if="unreadCount > 0">{{ unreadCount }} {{ $t('_xx._wwd') }}</span>
    </div>

    <!-- 消息列表 -->
    <div class="message-list" v-if="filteredMessages.length > 0">
      <div
        v-for="message in filteredMessages"
        :key="message.id"
        :class="['message-item', { unread: message.is_read == 0 }]"
        @click="toggleMessage(message)"
      >
        <div class="message-avatar">
          <i class="fas fa-bell"></i>
        </div>
        <div class="message-content">
          <div class="message-title">
            <span class="title-text">{{ message.title }}</span>
            <span class="message-time">{{ formatTime(message.create_time) }}</span>
          </div>
          <div class="message-preview" v-html="message.content"></div>
          <div class="message-detail" v-if="message.expanded">
            <div class="detail-content" v-html="message.content"></div>
          </div>
        </div>
        <div class="message-status">
          <span v-if="message.is_read == 0" class="unread-dot"></span>
        </div>
      </div>
    </div>

    <!-- 空状态 -->
    <div class="empty-state" v-else-if="!loading">
      <i class="fas fa-envelope-open"></i>
      <h3>{{ $t('_xx._wjl') }}</h3>
      <p>{{ $t('_xx._zsmyxx') }}</p>
    </div>

    <!-- 加载状态 -->
    <div class="loading-state" v-if="loading">
      <i class="fas fa-spinner fa-spin"></i>
      <span>{{ $t('_a._jzz') }}</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MessageCenter',
  data() {
    return {
      loading: false,
      messages: []
    }
  },
  computed: {
    filteredMessages() {
      return this.messages
    },
    unreadCount() {
      return this.messages.filter((m) => m.is_read == 0).length
    }
  },
  created() {
    this.getList()
  },
  methods: {
    getList() {
      this.loading = true
      this.$api.userMessage
        .getList()
        .then((res) => {
          this.messages = res.data || []
        })
        .catch(() => {
          this.messages = []
        })
        .finally(() => {
          this.loading = false
        })
    },

    toggleMessage(message) {
      message.expanded = !message.expanded
      // 标记已读
      if (message.is_read == 0) {
        this.markAsRead(message)
      }
    },

    markAsRead(message) {
      this.$api.userMessage
        .read({ id: message.id })
        .then(() => {
          message.is_read = 1
        })
        .catch(() => {})
    },

    formatTime(timeStr) {
      if (!timeStr) return ''
      const now = new Date()
      const time = new Date(timeStr)
      const diff = now - time
      const days = Math.floor(diff / (1000 * 60 * 60 * 24))
      const hours = Math.floor(diff / (1000 * 60 * 60))
      const minutes = Math.floor(diff / (1000 * 60))

      if (days > 0) return this.$t('_xx._ntq', { n: days })
      if (hours > 0) return this.$t('_xx._nxsq', { n: hours })
      if (minutes > 0) return this.$t('_xx._nfzq', { n: minutes })
      return this.$t('_xx._nggq')
    }
  }
}
</script>

<style scoped>
/* ============================================================
   消息中心 - 暗色主题
   ============================================================ */

.message-center {
  max-width: 1200px;
  margin: 0 auto;
  background: #0f1419;
  border-radius: 16px;
  border: 1px solid #1a1f26;
  min-height: 500px;
  overflow: hidden;
}

/* ============================================================
   头部
   ============================================================ */
.message-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 28px;
  border-bottom: 1px solid #1a1f26;
}

.message-header .title {
  font-size: 22px;
  font-weight: 600;
  color: #e8edf2;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
}

.message-header .title i {
  color: #e50914;
}

.unread-badge {
  padding: 4px 16px;
  border-radius: 20px;
  background: rgba(229, 9, 20, 0.15);
  color: #e50914;
  font-size: 13px;
  font-weight: 500;
}

/* ============================================================
   消息列表
   ============================================================ */
.message-list {
  padding: 4px 0;
}

.message-item {
  display: flex;
  padding: 16px 28px;
  border-bottom: 1px solid #141a20;
  cursor: pointer;
  transition: all 0.25s;
  position: relative;
}

.message-item:hover {
  background: rgba(255, 255, 255, 0.02);
}

.message-item.unread {
  background: rgba(229, 9, 20, 0.03);
}

.message-item.unread::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: #e50914;
}

/* ============================================================
   头像/图标
   ============================================================ */
.message-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(229, 9, 20, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  flex-shrink: 0;
}

.message-avatar i {
  color: #e50914;
  font-size: 16px;
}

/* ============================================================
   消息内容
   ============================================================ */
.message-content {
  flex: 1;
  min-width: 0;
}

.message-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.title-text {
  font-weight: 600;
  color: #e8edf2;
  font-size: 15px;
}

.message-time {
  font-size: 12px;
  color: #5a6672;
  flex-shrink: 0;
  margin-left: 12px;
}

.message-preview {
  color: #8a949f;
  font-size: 14px;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.message-detail {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.04);
}

.detail-content {
  color: #b0bcc8;
  font-size: 14px;
  line-height: 1.8;
}

/* ============================================================
   状态标识
   ============================================================ */
.message-status {
  display: flex;
  align-items: center;
  margin-left: 12px;
  flex-shrink: 0;
}

.unread-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #e50914;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
}

/* ============================================================
   空状态
   ============================================================ */
.empty-state {
  text-align: center;
  padding: 80px 20px;
}

.empty-state i {
  font-size: 56px;
  color: #2a3138;
  margin-bottom: 16px;
}

.empty-state h3 {
  color: #5a6672;
  font-size: 18px;
  margin: 0 0 8px;
}

.empty-state p {
  color: #3a4652;
  font-size: 14px;
  margin: 0;
}

/* ============================================================
   加载状态
   ============================================================ */
.loading-state {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  color: #e50914;
  gap: 10px;
}

.loading-state i {
  font-size: 24px;
}

.loading-state span {
  color: #8a949f;
  font-size: 14px;
}

/* ============================================================
   响应式
   ============================================================ */
@media (max-width: 768px) {
  .message-center {
    border-radius: 12px;
    min-height: 400px;
  }

  .message-header {
    padding: 16px 20px;
    flex-wrap: wrap;
    gap: 8px;
  }

  .message-header .title {
    font-size: 18px;
  }

  .unread-badge {
    font-size: 12px;
    padding: 3px 12px;
  }

  .message-item {
    padding: 14px 20px;
  }

  .message-avatar {
    width: 36px;
    height: 36px;
    margin-right: 12px;
  }

  .message-avatar i {
    font-size: 14px;
  }

  .title-text {
    font-size: 14px;
  }

  .message-preview {
    font-size: 13px;
  }

  .empty-state {
    padding: 60px 20px;
  }

  .empty-state i {
    font-size: 44px;
  }
}

@media (max-width: 480px) {
  .message-header {
    padding: 14px 16px;
  }

  .message-header .title {
    font-size: 16px;
  }

  .unread-badge {
    font-size: 11px;
    padding: 2px 10px;
  }

  .message-item {
    padding: 12px 16px;
  }

  .message-avatar {
    width: 32px;
    height: 32px;
    margin-right: 10px;
  }

  .message-avatar i {
    font-size: 12px;
  }

  .title-text {
    font-size: 13px;
  }

  .message-preview {
    font-size: 12px;
  }

  .message-time {
    font-size: 11px;
  }

  .detail-content {
    font-size: 13px;
  }
}
</style>